/**
 * TrendsSection Component
 * Displays time-series charts for voicebot costs and chatbot messages
 */

import { useState } from 'react';
import { MetricChart, ChartDataPoint } from './MetricChart';
import { useMetrics } from '@/hooks/useAnalytics';
import type { MetricsQueryParams } from '@/types/analytics';
import { Button } from '@/components/ui/button';
import { DollarSign, MessageSquare, Phone, TrendingUp } from 'lucide-react';

interface TrendsSectionProps {
  dateRange: MetricsQueryParams;
}

type ChartView = 'voicebot_cost' | 'voicebot_calls' | 'chatbot_messages' | 'chatbot_leads';

const CHART_OPTIONS = [
  { value: 'voicebot_cost' as ChartView, label: 'Voicebot Cost', icon: DollarSign, color: '#ef4444' },
  { value: 'voicebot_calls' as ChartView, label: 'Voicebot Calls', icon: Phone, color: '#3b82f6' },
  { value: 'chatbot_messages' as ChartView, label: 'Chatbot Messages', icon: MessageSquare, color: '#10b981' },
  { value: 'chatbot_leads' as ChartView, label: 'Chatbot Leads', icon: TrendingUp, color: '#8b5cf6' },
];

export function TrendsSection({ dateRange }: TrendsSectionProps) {
  const [activeChart, setActiveChart] = useState<ChartView>('voicebot_cost');

  // Fetch voicebot metrics
  const { data: voicebotData, isLoading: voicebotLoading } = useMetrics({
    ...dateRange,
    metricType: 'voicebot_',
  });

  // Fetch chatbot metrics
  const { data: chatbotData, isLoading: chatbotLoading } = useMetrics({
    ...dateRange,
    metricType: 'chatbot_',
  });

  // Helper to format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Transform metric records to chart data points
  const getChartData = (): ChartDataPoint[] => {
    let records: any[] = [];

    switch (activeChart) {
      case 'voicebot_cost':
        records = voicebotData?.metrics['voicebot_daily_cost'] || [];
        break;
      case 'voicebot_calls':
        records = voicebotData?.metrics['voicebot_daily_calls'] || [];
        break;
      case 'chatbot_messages':
        records = chatbotData?.metrics['chatbot_daily_messages'] || [];
        break;
      case 'chatbot_leads':
        records = chatbotData?.metrics['chatbot_daily_leads'] || [];
        break;
      default:
        records = [];
    }

    if (!records || records.length === 0) return [];

    return records.map((record: any) => ({
      date: formatDate(record.date),
      value: record.value,
    }));
  };

  // Get chart configuration
  const activeChartConfig = CHART_OPTIONS.find((opt) => opt.value === activeChart);
  const chartData = getChartData();
  const isLoading = voicebotLoading || chatbotLoading;

  // Determine Y-axis label and chart type
  const getYAxisLabel = (): string => {
    switch (activeChart) {
      case 'voicebot_cost':
        return 'Cost (USD)';
      case 'voicebot_calls':
        return 'Calls';
      case 'chatbot_messages':
        return 'Messages';
      case 'chatbot_leads':
        return 'Leads';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-4">
      {/* Chart selector buttons */}
      <div className="flex flex-wrap gap-2">
        {CHART_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <Button
              key={option.value}
              variant={activeChart === option.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveChart(option.value)}
              className="flex items-center gap-2"
            >
              <Icon className="h-4 w-4" />
              {option.label}
            </Button>
          );
        })}
      </div>

      {/* Main chart */}
      <MetricChart
        title={activeChartConfig?.label || 'Trends'}
        description={`Daily ${activeChartConfig?.label.toLowerCase()} over the selected period`}
        data={chartData}
        type="area"
        dataKey="value"
        xAxisKey="date"
        yAxisLabel={getYAxisLabel()}
        color={activeChartConfig?.color}
        loading={isLoading}
        height={350}
      />

      {/* Summary stats for active chart */}
      {!isLoading && chartData.length > 0 && (
        <div className="grid grid-cols-3 gap-4 p-4 border border-border rounded-lg bg-muted/30">
          <div>
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="text-lg font-bold">
              {chartData.reduce((sum, point) => sum + point.value, 0).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Average</p>
            <p className="text-lg font-bold">
              {(chartData.reduce((sum, point) => sum + point.value, 0) / chartData.length).toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Peak</p>
            <p className="text-lg font-bold">
              {Math.max(...chartData.map((point) => point.value)).toLocaleString()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
